import { useState, useEffect, ReactNode } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { LoadingOverlay } from './Loading'
import AuthModal from './auth/AuthModal'
import { isAdminUser, elevateToAdminSession } from '../utils/admin'
import SEOHead from './SEOHead'

/**
 * Replaces ProtectedRoute on /admin routes.
 * Instead of redirecting unauthenticated users to /, it shows an inline
 * login prompt that slides up from the bottom — matching the newsletter
 * signup animation on the homepage.
 *
 * When someone is signed in but not an admin (e.g. an affiliate account),
 * the same panel offers a "Switch to Admin" action that elevates the
 * session into the admin account.
 */
export default function AdminAuthGate({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const [show, setShow] = useState(false)
  const [switching, setSwitching] = useState(false)
  const [switchError, setSwitchError] = useState<string | null>(null)

  const isAdmin = isAdminUser(user)

  // Trigger the slide-up entrance after mount
  useEffect(() => {
    const timer = setTimeout(() => setShow(true), 50)
    return () => clearTimeout(timer)
  }, [])

  // Auto-open the sign-in modal when landing on /admin without a session
  useEffect(() => {
    if (!loading && !user) {
      setAuthModalOpen(true)
    }
  }, [user, loading])

  const handleSwitchToAdmin = async () => {
    setSwitching(true)
    setSwitchError(null)
    const { error } = await elevateToAdminSession()
    if (error) {
      setSwitchError(error.message)
      setSwitching(false)
      return
    }
    // Full reload so AuthContext re-initializes with the admin session
    window.location.href = '/admin'
  }

  // The gate's own branches need a title: /admin isn't pre-rendered, so Vercel's
  // catch-all serves it the noindex shell, whose <title> is
  // "Not Found | THE LOST+UNFOUNDS" (scripts/generate-404-html.ts). Admin.tsx has
  // a SEOHead, but it only mounts once the gate lets you through — so while this
  // is showing the loading overlay or the sign-in panel the tab read "Not Found".
  // These branches never render alongside {children}, so there is no double title.
  if (loading) {
    return (
      <>
        <SEOHead title="Admin" description="Administrative dashboard for THE LOST+UNFOUNDS." canonicalPath="/admin" noIndex={true} />
        <LoadingOverlay />
      </>
    )
  }

  if (!user || !isAdmin) {
    return (
      <>
        <SEOHead title="Admin" description="Administrative dashboard for THE LOST+UNFOUNDS." canonicalPath="/admin" noIndex={true} />
        <div
          style={{
            opacity: show ? 1 : 0,
            visibility: show ? 'visible' : 'hidden',
            position: 'fixed',
            left: '50%',
            top: show ? '50%' : '100%',
            transform: show ? 'translate(-50%, -50%)' : 'translate(-50%, 0)',
            width: '100%',
            maxWidth: 'min(400px, 90vw)',
            padding: '0 1rem',
            zIndex: 40,
            transition: 'opacity 1.5s ease-in-out, transform 1.5s ease-in-out, visibility 0s linear',
            pointerEvents: show ? 'auto' : 'none',
            willChange: 'opacity, transform',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1.5rem',
          }}
        >
          <div className="text-center space-y-6 w-full">
            <div className="space-y-2">
              <p className="text-white/30 text-[10px] font-black uppercase tracking-[0.3em]">
                Restricted Area
              </p>
              <h1 className="text-white text-2xl font-black uppercase tracking-widest">
                Admin Access
              </h1>
            </div>
            {!user ? (
              <button
                onClick={() => setAuthModalOpen(true)}
                className="inline-flex items-center px-8 py-4 bg-white text-black font-black uppercase tracking-widest text-xs hover:bg-zinc-200 transition-all active:scale-95"
              >
                Sign In
              </button>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <p className="text-white/40 text-[10px] font-bold uppercase tracking-[0.2em]">
                  Signed in as {user.email}
                </p>
                <button
                  onClick={handleSwitchToAdmin}
                  disabled={switching}
                  className="inline-flex items-center px-8 py-4 bg-white text-black font-black uppercase tracking-widest text-xs hover:bg-zinc-200 transition-all active:scale-95 disabled:opacity-50"
                >
                  {switching ? 'Switching…' : 'Switch to Admin'}
                </button>
                {switchError && (
                  <p className="text-red-400 text-[10px] font-bold uppercase tracking-wider max-w-[280px]">
                    {switchError}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        <AuthModal
          isOpen={authModalOpen}
          onClose={() => setAuthModalOpen(false)}
          googleOnly
          title="Admin Sign In"
          message="Sign in with the admin Google account."
        />
      </>
    )
  }

  return <>{children}</>
}
